import { useEffect, useRef, useState } from 'react';
import type { Masjid } from '@noorview/core';
import { PIN_MAX, checkExitPin } from '@noorview/core';

/**
 * The exit PIN pad on a locked display (shown when someone touches the screen or presses a key). The right PIN
 * releases the display for two minutes; five wrong tries wait a minute. Works with touch, a mouse, a keyboard and a
 * TV remote: its number keys, or the arrows and OK (most remotes have no number keys).
 */
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'del', '0', 'ok'];
export const RELEASE_MS = 2 * 60_000;
const WAIT_MS = 60_000, IDLE_MS = 30_000;
let wrong = 0, waitUntil = 0;

export function ExitPad({ masjid, onClose, onRelease }: { masjid: Pick<Masjid, 'id' | 'exitPinHash'>; onClose: () => void; onRelease: () => void }) {
  const [pin, setPin] = useState('');
  const [msg, setMsg] = useState('');
  const [shake, setShake] = useState(0);
  const [now, setNow] = useState(Date.now());
  const waiting = waitUntil > now;
  // The arrows move between the keys (3 across, 4 down, then Cancel); OK presses the one that's lit.
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  const focusAt = (i: number) => buttons.current[Math.max(0, Math.min(KEYS.length, i))]?.focus();
  useEffect(() => { focusAt(0); }, []);
  const move = (key: string) => {
    const i = buttons.current.findIndex((b) => b === document.activeElement);
    if (i < 0) { focusAt(0); return; }
    if (i === KEYS.length) { if (key === 'ArrowUp') focusAt(10); return; }
    const r = Math.floor(i / 3), c = i % 3;
    if (key === 'ArrowLeft' && c > 0) focusAt(i - 1);
    else if (key === 'ArrowRight' && c < 2) focusAt(i + 1);
    else if (key === 'ArrowUp' && r > 0) focusAt(i - 3);
    else if (key === 'ArrowDown') focusAt(r < 3 ? i + 3 : KEYS.length);
  };

  const enter = (p: string) => {
    if (waitUntil > Date.now()) return;
    if (checkExitPin(masjid, p)) { wrong = 0; onRelease(); return; }
    wrong += 1; setPin(''); setShake((n) => n + 1);
    if (wrong >= 5) { wrong = 0; waitUntil = Date.now() + WAIT_MS; setNow(Date.now()); setMsg('Too many wrong tries. Wait a minute, then try again.'); }
    else setMsg('That PIN isn’t right.');
  };
  const press = (k: string) => {
    if (waitUntil > Date.now()) return;
    setMsg('');
    if (k === 'del') { setPin((p) => p.slice(0, -1)); return; }
    if (k === 'ok') { enter(pin); return; }
    setPin((p) => (p.length >= PIN_MAX ? p : p + k));
  };

  // Closes by itself when nobody touches it.
  useEffect(() => { const t = setTimeout(onClose, IDLE_MS); return () => clearTimeout(t); }, [pin, onClose]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Typed on a keyboard: let go of the lit key, so Enter then enters the PIN (instead of pressing that key).
      if (/^\d$/.test(e.key)) { press(e.key); (document.activeElement as HTMLElement | null)?.blur(); }
      else if (e.key.startsWith('Arrow')) { e.preventDefault(); move(e.key); }
      // OK on a lit key presses that key (the browser clicks it); Enter with nothing lit enters the PIN.
      else if (e.key === 'Enter') { if (!buttons.current.includes(document.activeElement as HTMLButtonElement)) press('ok'); }
      else if (e.key === 'Backspace') press('del');
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div className="exit-pad-scrim" role="dialog" aria-modal="true" aria-label="Exit PIN">
      <div className="exit-pad" key={shake} data-shake={shake ? '' : undefined}>
        <b>Enter the exit PIN</b>
        <span className="hint">{waiting ? `Try again in ${Math.ceil((waitUntil - now) / 1000)} seconds` : msg || 'Set by your masjid’s admin in Settings › Screen lock'}</span>
        <div className="exit-dots" aria-label={`${pin.length} digits entered`}>{Array.from({ length: Math.max(4, pin.length) }, (_, i) => <i key={i} className={i < pin.length ? 'on' : ''} />)}</div>
        <div className="exit-keys">
          {KEYS.map((k, i) => (
            <button key={k} ref={(el) => { buttons.current[i] = el; }} type="button" disabled={waiting} className={k === 'ok' ? 'ok' : k === 'del' ? 'del' : ''} onClick={() => press(k)} aria-label={k === 'del' ? 'Delete' : k === 'ok' ? 'Enter' : k}>
              {k === 'del' ? '⌫' : k === 'ok' ? 'Enter' : k}
            </button>
          ))}
        </div>
        <button ref={(el) => { buttons.current[KEYS.length] = el; }} type="button" className="exit-cancel" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
